import React, { createContext, useContext, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

import ConfigService from "../services/ConfigService";

import PageNotFound from "../components/pageNotFound/PageNotFound";
import LoadingPage from "../components/loadingPage/LoadingPage";
import { setPromoColors } from "./utils";
import { createFontFaceStyle } from "./fontFaces";
import { promoConfigQuery } from "./promoConfigQuery";
import { isSweepOrg } from "../utils/sweepUtils";
import { isSoccerbetOrg } from "../utils/brandUtils";

import i18n from "../i18n";
import type { PromoConfig } from "../types/promoConfig";

const ConfigContext = createContext<PromoConfig | null>(null);

const setCSSVariables = (paramKey: string, paramValue: string) => {
  document.documentElement.style.setProperty(`${paramKey}`, `${paramValue}`);
};

const setBackgroundVariable = (name: string, uri: unknown) => {
  if (uri) {
    setCSSVariables(name, `url(${uri})`);
  } else {
    document.documentElement.style.removeProperty(name);
  }
};

const setPromoBackground = (bgString: string | undefined) => {
  if (!bgString || bgString.length === 0) {
    return;
  }

  let bgObj;
  try {
    bgObj = JSON.parse(bgString);
  } catch {
    console.warn("Invalid promo background JSON");
    return;
  }

  setBackgroundVariable("--background-image-web", bgObj?.["desktop"]?.[0]?.uri);
  setBackgroundVariable("--background-image-mobile", bgObj?.["mobile"]?.[0]?.uri);
};

async function loadFonts(promoConfig: PromoConfig, lang: string) {
  let selectedLang;
  let selectedTitleLang;

  if (Array.isArray(promoConfig?.fonts) && promoConfig?.fonts?.length > 0) {
    if (lang) {
      selectedLang = promoConfig.fonts.find((font: any) => font.lang === lang);
    }
    if (!selectedLang) {
      selectedLang = promoConfig.fonts.find((font: any) => font.default);
    }
    if (!selectedLang) {
      selectedLang = promoConfig.fonts[0];
    }

    const fontFaceStyles = Object.entries(selectedLang?.fontFamily ?? {});
    fontFaceStyles.forEach(([fontWeight, fontPath]) => {
      createFontFaceStyle(typeof fontPath === "string" ? fontPath : "", fontWeight);
    });
  }

  if (Array.isArray(promoConfig?.titleFont) && promoConfig?.titleFont?.length > 0) {
    if (lang) {
      selectedTitleLang = promoConfig.titleFont.find((font: any) => font.lang === lang);
    }
    if (!selectedTitleLang) {
      selectedTitleLang = promoConfig.titleFont.find((font: any) => font.default);
    }
    if (!selectedTitleLang) {
      selectedTitleLang = promoConfig.titleFont[0];
    }

    const titleFontFaceStyles = Object.entries(selectedTitleLang?.fontFamily ?? {});
    titleFontFaceStyles.forEach(([fontWeight, fontPath]) => {
      createFontFaceStyle(typeof fontPath === "string" ? fontPath : "", fontWeight, true);
    });
  }
}

export const ConfigProvider = ({
  children,
  promoId,
  loadingFallback,
  errorFallback,
}: {
  children: React.ReactNode;
  promoId?: string;
  loadingFallback?: React.ReactNode;
  errorFallback?: React.ReactNode;
}) => {
  const promo_id = promoId || ConfigService.getPromoId();
  const { data: promoConfig, isLoading, isError } = useQuery(promoConfigQuery(promo_id));
  const isReady = !isLoading && !isError && !!promoConfig && typeof promoConfig === "object";
  const language = i18n.language;

  // Applied while rendering, before the children render: some of them read the theme colors
  // from <html> in their own render (NavIcon).
  useMemo(() => {
    if (!isReady) return;
    loadFonts(promoConfig, language);
    setPromoColors(promoConfig.layout);
    setPromoBackground(promoConfig.components?.background);
  }, [isReady, promoConfig, language]);

  const contextValue = useMemo(
    () =>
      isReady
        ? {
            ...promoConfig,
            isSweep: isSweepOrg(promoConfig?.orgUuid),
            isSoccerbet: isSoccerbetOrg(promoConfig?.orgUuid),
          }
        : null,
    [isReady, promoConfig]
  );

  if (
    !isLoading &&
    (isError || !promoConfig || typeof promoConfig !== "object" || promoConfig === null)
  ) {
    return <>{errorFallback ?? <PageNotFound />}</>;
  }
  if (isLoading) {
    return <>{loadingFallback ?? <LoadingPage />}</>;
  }

  return <ConfigContext.Provider value={contextValue}>{children}</ConfigContext.Provider>;
};

export const useConfig = () => {
  return useContext(ConfigContext);
};

export { ConfigContext };
